import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useAppSelector, useAppDispatch } from '../../hooks/redux';
import { fetchProjectMilestones, createMilestone, updateMilestone, deleteMilestone, restoreMilestone } from '../../store/slices/milestonesSlice';
import LoadingSpinner from '../UI/LoadingSpinner';
import Button from '../UI/Button';
import {
  MilestoneProgressOverview,
  MilestoneVerticalStepper,
  Milestone,
} from './Milestones/index';
import { InlineEditMilestone } from '../UI/InlineEdit';
import MilestoneForm from './Milestones/MilestoneForm';
import { MilestoneFormData } from './Milestones/index';

interface ProjectMilestonesProps {
  projectId?: string;
  milestones?: Milestone[]; // Optional: for backward compatibility
  onMilestoneUpdate?: (milestoneId: string, milestone: Partial<Milestone>) => Promise<void> | void;
  disabled?: boolean;
  isOwner?: boolean;
}

const ProjectMilestones: React.FC<ProjectMilestonesProps> = ({ 
  projectId: propProjectId,
  milestones: propMilestones,
  onMilestoneUpdate,
  disabled = false,
  isOwner = false,
}) => {
  const { id: paramProjectId } = useParams<{ id: string }>();
  const projectId = propProjectId || paramProjectId || '';
  const dispatch = useAppDispatch();
  const { items, projectMilestones: projectMilestonesMap, isLoading } = useAppSelector((state) => state.milestones);
  const [showAddForm, setShowAddForm] = useState(false);
  const [milestoneForm, setMilestoneForm] = useState<Partial<MilestoneFormData>>({});

  useEffect(() => {
    if (projectId && !propMilestones) {
      dispatch(fetchProjectMilestones({ projectId, includeDeleted: isOwner }));
    }
  }, [dispatch, projectId, propMilestones, isOwner]);

  // Use prop milestones if provided (backward compatibility), otherwise use Redux state
  const allMilestones = propMilestones || (projectId && projectMilestonesMap[projectId]
    ? projectMilestonesMap[projectId].map((id) => items[id]).filter(Boolean) as Milestone[]
    : []);

  // Filter: show deleted only to owners, hide from others
  const activeMilestones = isOwner 
    ? allMilestones.filter((m) => !m.deletedAt)
    : allMilestones.filter((m) => !m.deletedAt);
  const deletedMilestones = isOwner 
    ? allMilestones.filter((m) => m.deletedAt)
    : [];

  const handleMilestoneUpdate = async (milestoneId: string, data: Partial<Milestone>) => {
    if (onMilestoneUpdate) {
      await onMilestoneUpdate(milestoneId, data);
    } else {
      // Default: use Redux action
      await dispatch(updateMilestone({ id: milestoneId, data })).unwrap();
      // Refresh milestones list after successful update
      if (projectId) {
        await dispatch(fetchProjectMilestones({ projectId, includeDeleted: isOwner }));
      }
    }
  };

  const handleAddMilestone = async () => {
    if (!milestoneForm.title || !milestoneForm.scheduledDate || !projectId) return;
    
    try {
      const milestoneData: Partial<Milestone> & { projectId: string } = {
        projectId,
        title: milestoneForm.title!,
        description: milestoneForm.description,
        scheduledDate: milestoneForm.scheduledDate,
        status: milestoneForm.status || 'pending',
        delayReason: milestoneForm.delayReason,
        earlyCompletion: milestoneForm.earlyCompletion || false,
        earlyCompletionReason: milestoneForm.earlyCompletionReason,
      };
      await dispatch(createMilestone(milestoneData));
      setShowAddForm(false);
      setMilestoneForm({});
    } catch (error) {
      console.error('Error creating milestone:', error);
    }
  };

  const handleDeleteMilestone = async (milestoneId: string) => {
    if (!confirm('Are you sure you want to delete this milestone?')) return;
    try {
      await dispatch(deleteMilestone(milestoneId));
    } catch (error) {
      console.error('Error deleting milestone:', error);
    }
  };

  const handleRestoreMilestone = async (milestoneId: string) => {
    try {
      await dispatch(restoreMilestone(milestoneId));
    } catch (error) {
      console.error('Error restoring milestone:', error);
    }
  };

  if (isLoading && !propMilestones) {
    return <LoadingSpinner size="sm" text="Loading milestones..." />;
  }

  const completedCount = activeMilestones.filter((m) => m.status === 'completed').length;
  const totalCount = activeMilestones.length;

  return (
    <div className="space-y-6">
      {!disabled && isOwner && (
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold">Milestones</h3>
          {!showAddForm && (
            <Button
              type="button"
              size="sm"
              onClick={() => setShowAddForm(true)}
            >
              Add Milestone
            </Button>
          )}
        </div>
      )}

      {showAddForm && !disabled && isOwner && (
        <div className="border rounded-lg p-4 bg-gray-50">
          <MilestoneForm
            formData={{
              title: milestoneForm.title || '',
              description: milestoneForm.description,
              scheduledDate: milestoneForm.scheduledDate || new Date().toISOString().split('T')[0],
              status: milestoneForm.status || 'pending',
              delayReason: milestoneForm.delayReason,
              earlyCompletion: milestoneForm.earlyCompletion,
              earlyCompletionReason: milestoneForm.earlyCompletionReason,
            } as MilestoneFormData}
            onFormDataChange={(data) => setMilestoneForm((prev) => ({ ...prev, ...data }))}
            onSubmit={handleAddMilestone}
            onCancel={() => {
              setShowAddForm(false);
              setMilestoneForm({});
            }}
            submitLabel="Add"
          />
        </div>
      )}

      {activeMilestones.length === 0 && deletedMilestones.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          <p>No milestones defined</p>
        </div>
      ) : (
        <>
          {activeMilestones.length > 0 && (
            <>
              <MilestoneProgressOverview completedCount={completedCount} totalCount={totalCount} />
              <div className="space-y-4">
                {activeMilestones.map((milestone) => {
                  const milestoneId = milestone._id || milestone.id || '';
                  return (
                    <div key={milestoneId} className="relative">
                      <InlineEditMilestone
                        milestone={milestone}
                        onSave={(id, updated) => handleMilestoneUpdate(id, updated)}
                        disabled={disabled}
                      />
                      {!disabled && isOwner && (
                        <button
                          type="button"
                          onClick={() => handleDeleteMilestone(milestoneId)}
                          className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200 bg-red-50 hover:bg-red-100 text-red-600 rounded-full p-1"
                          aria-label="Delete milestone"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            </>
          )}

          {deletedMilestones.length > 0 && isOwner && (
            <div className="mt-8 border-t pt-6">
              <h4 className="text-sm font-medium text-gray-500 mb-4">Deleted Milestones</h4>
              <div className="space-y-3">
                {deletedMilestones.map((milestone) => {
                  const milestoneId = milestone._id || milestone.id || '';
                  return (
                    <div
                      key={milestoneId}
                      className="border rounded-lg p-4 bg-gray-50 opacity-60 relative"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <h4 className="font-medium text-gray-700 line-through">{milestone.title}</h4>
                            <span className="text-xs bg-red-100 text-red-700 px-2 py-1 rounded">Deleted</span>
                          </div>
                          {milestone.description && (
                            <p className="text-sm text-gray-500 mt-1 line-through">{milestone.description}</p>
                          )}
                        </div>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => handleRestoreMilestone(milestoneId)}
                        >
                          Restore
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ProjectMilestones;

